import { useCallback, useEffect, useMemo, useState } from 'react'

import { AnimatePresence, motion } from 'framer-motion'

import { Icon } from '@components/icons/Icon'
import { IconButton } from '@components/ui/IconButton'
import { Input } from '@components/ui/Input'
import { type OAuthProviderType, useLoginWithEmail, useLoginWithOAuth } from '@privy-io/react-auth'

interface LoginSheetProps {
  open: boolean
  onClose?: () => void
}

type SheetView = 'main' | 'email'
type EmailStep = 'input' | 'code'

type OAuthOption = {
  id: OAuthProviderType
  label: string
  description: string
  badge: {
    text: string
    className: string
  }
}

const OAUTH_OPTIONS: OAuthOption[] = [
  {
    id: 'google',
    label: 'Googleでログイン',
    description: 'Googleアカウントで素早くログインできます',
    badge: { text: 'G', className: 'bg-white text-[#4285f4] border border-zinc-200' },
  },
  {
    id: 'twitter',
    label: 'X（旧Twitter）でログイン',
    description: 'Xアカウントを使って認証します',
    badge: { text: 'X', className: 'bg-black text-white' },
  },
]

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const PRIVY_ERROR_MESSAGES: Record<string, string> = {
  user_cancelled: 'ログインがキャンセルされました。',
  invalid_credentials: '入力内容に誤りがあります。再度ご確認ください。',
}

const spinner = (
  <div
    className="size-5 animate-spin rounded-full border-2 border-zinc-300 border-t-transparent"
    aria-hidden
  />
)

const getErrorMessage = (code: string) =>
  PRIVY_ERROR_MESSAGES[code] ?? `ログインに失敗しました。（コード: ${code}）`

const extractMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message.trim() || 'エラーが発生しました。'
  }
  if (typeof error === 'string') {
    return error
  }
  return 'エラーが発生しました。'
}

export const LoginSheet: React.FC<LoginSheetProps> = ({ open, onClose }) => {
  const [view, setView] = useState<SheetView>('main')
  const [emailStep, setEmailStep] = useState<EmailStep>('input')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [feedback, setFeedback] = useState<{ message: string; tone: 'info' | 'error' } | null>(null)
  const [isCompletingLogin, setIsCompletingLogin] = useState(false)

  const resetState = useCallback(() => {
    setView('main')
    setEmailStep('input')
    setEmail('')
    setCode('')
    setFeedback(null)
    setIsCompletingLogin(false)
  }, [])

  useEffect(() => {
    if (!open) {
      resetState()
    }
  }, [open, resetState])

  const callbacks = useMemo(
    () => ({
      onComplete: () => {
        setFeedback(null)
        setIsCompletingLogin(true)
      },
      onError: (errorCode: string) => {
        setIsCompletingLogin(false)
        setFeedback({ message: getErrorMessage(errorCode), tone: 'error' })
      },
    }),
    []
  )

  const { initOAuth, loading: oauthLoading } = useLoginWithOAuth(callbacks)
  const { sendCode, loginWithCode, state: emailState } = useLoginWithEmail(callbacks)

  useEffect(() => {
    if (emailState.status === 'awaiting-code-input') {
      setEmailStep('code')
      setFeedback({
        message: `${email}に確認コードを送信しました。届いたコードを入力してください。`,
        tone: 'info',
      })
    }
    if (emailState.status === 'error') {
      setFeedback({ message: extractMessage(emailState.error), tone: 'error' })
    }
  }, [emailState, email])

  const isEmailValid = EMAIL_REGEX.test(email)
  const isSendingEmail = emailState.status === 'sending-code'
  const isSubmittingCode = emailState.status === 'submitting-code'

  const handleOAuth = async (provider: OAuthOption['id']) => {
    setFeedback(null)
    try {
      await initOAuth({ provider })
    } catch (error) {
      setIsCompletingLogin(false)
      setFeedback({ message: extractMessage(error), tone: 'error' })
    }
  }

  const handleSendCode = async () => {
    if (!isEmailValid) {
      setFeedback({ message: '有効なメールアドレスを入力してください。', tone: 'error' })
      return
    }
    setFeedback(null)
    try {
      await sendCode({ email })
    } catch (error) {
      setFeedback({ message: extractMessage(error), tone: 'error' })
    }
  }

  const handleSubmitCode = async () => {
    if (!code.trim()) {
      setFeedback({ message: 'メールに届いたコードを入力してください。', tone: 'error' })
      return
    }
    setFeedback(null)
    try {
      await loginWithCode({ code })
    } catch (error) {
      setIsCompletingLogin(false)
      setFeedback({ message: extractMessage(error), tone: 'error' })
    }
  }

  const resendCode = async () => {
    if (emailState.status === 'awaiting-code-input' || emailState.status === 'error') {
      await handleSendCode()
    }
  }

  const canResend =
    emailState.status === 'awaiting-code-input' ||
    emailState.status === 'error' ||
    emailState.status === 'initial'

  const title =
    view === 'main'
      ? 'ログイン'
      : emailStep === 'input'
        ? 'メールアドレスでログイン'
        : '確認コードの入力'
  const description =
    view === 'main'
      ? 'ARCS Walletにログインする方法を選択してください'
      : emailStep === 'input'
        ? 'ログインに使用するメールアドレスを入力してください'
        : `${email}に送信された6桁のコードを入力してください`

  return (
    <AnimatePresence initial={false}>
      {open ? (
        <motion.div
          key="login-sheet"
          className="fixed inset-0 z-50 flex w-full justify-center"
          aria-hidden={!open}
          style={{ pointerEvents: open ? 'auto' : 'none' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          <motion.button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="ログインモーダルを閉じる"
            onClick={onClose}
            tabIndex={open ? 0 : -1}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          />
          <motion.dialog
            className="relative mt-auto mb-0 flex w-full max-w-[480px] justify-center border-0 bg-transparent px-0"
            open
            aria-labelledby="login-sheet-title"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 420, damping: 32, mass: 0.8 }}
          >
            <div className="flex w-full flex-col rounded-t-[32px] bg-white pb-10 pt-2 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]">
              <div className="flex justify-center">
                <div className="h-1.5 w-12 rounded-full bg-zinc-300" aria-hidden />
              </div>
              <header className="mt-3 flex items-center justify-between px-6">
                <div className="inline-flex h-10 w-10 items-center justify-center">
                  {view === 'main' ? null : (
                    <IconButton
                      aria-label="前の画面に戻る"
                      icon="Back"
                      onClick={() => {
                        if (view === 'email' && emailStep === 'code') {
                          setEmailStep('input')
                          setFeedback(null)
                        } else {
                          setView('main')
                          setFeedback(null)
                        }
                      }}
                      tabIndex={open ? 0 : -1}
                    />
                  )}
                </div>
                <p
                  id="login-sheet-title"
                  className="text-[20px] font-bold leading-none text-zinc-900"
                >
                  {title}
                </p>
                <IconButton
                  aria-label="ログインモーダルを閉じる"
                  icon="Close"
                  onClick={onClose}
                  tabIndex={open ? 0 : -1}
                />
              </header>
              <motion.div
                key={`${view}-${emailStep}`}
                initial={{ opacity: 0, x: view === 'main' ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: view === 'main' ? -20 : 20 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                className="mt-6 flex flex-col gap-6 px-6"
              >
                <p className="text-[14px] font-medium text-zinc-500">{description}</p>
                {view === 'main' ? (
                  <div className="flex flex-col gap-4">
                    {OAUTH_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleOAuth(option.id)}
                        className="flex w-full items-center justify-between gap-3 rounded-2xl border border-zinc-100 px-4 py-3 text-left shadow-[0px_4px_8px_rgba(0,0,0,0.02)] transition-colors duration-150 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-70"
                        disabled={oauthLoading || isCompletingLogin}
                      >
                        <span className="flex items-center gap-3">
                          <span
                            className={`flex size-10 items-center justify-center rounded-full text-[16px] font-semibold ${option.badge.className}`}
                          >
                            {option.badge.text}
                          </span>
                          <span className="flex flex-col">
                            <span className="text-[16px] font-semibold text-zinc-900">
                              {option.label}
                            </span>
                            <span className="text-[13px] text-zinc-400">{option.description}</span>
                          </span>
                        </span>
                        <span className="flex items-center gap-2">
                          {oauthLoading || isCompletingLogin ? (
                            spinner
                          ) : (
                            <Icon content="ChevronRight" />
                          )}
                        </span>
                      </button>
                    ))}
                    <div className="h-px bg-zinc-100" aria-hidden />
                    <button
                      type="button"
                      onClick={() => {
                        setView('email')
                        setEmailStep('input')
                        setFeedback(null)
                      }}
                      className="flex w-full items-center justify-between gap-3 rounded-2xl border border-zinc-100 px-4 py-3 text-left shadow-[0px_4px_8px_rgba(0,0,0,0.02)] transition-colors duration-150 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                    >
                      <span className="flex items-center gap-3">
                        <span className="flex size-10 items-center justify-center rounded-full bg-blue-50 text-[16px] font-semibold text-blue-500">
                          <Icon content="Mail" />
                        </span>
                        <span className="flex flex-col">
                          <span className="text-[16px] font-semibold text-zinc-900">
                            メールアドレスでログイン
                          </span>
                          <span className="text-[13px] text-zinc-400">
                            ワンタイムコードで認証します
                          </span>
                        </span>
                      </span>
                      <Icon content="ChevronRight" />
                    </button>
                  </div>
                ) : emailStep === 'input' ? (
                  <div className="flex flex-col gap-4">
                    <Input
                      label="メールアドレス"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="your@example.com"
                      type="email"
                      autoComplete="email"
                      error={
                        !isEmailValid && feedback?.tone === 'error' ? feedback.message : undefined
                      }
                    />
                    <button
                      type="button"
                      onClick={handleSendCode}
                      className="flex w-full items-center justify-center rounded-2xl bg-blue-500 px-4 py-3 text-[16px] font-semibold text-white shadow-[0px_8px_16px_rgba(59,130,246,0.25)] transition-opacity duration-150 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-70"
                      disabled={!isEmailValid || isSendingEmail}
                    >
                      {isSendingEmail ? '送信中…' : '確認コードを送信'}
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    <Input
                      label="確認コード"
                      value={code}
                      onChange={(event) => setCode(event.target.value.replace(/[^0-9A-Za-z]/g, ''))}
                      placeholder="6桁のコード"
                      inputMode="numeric"
                    />
                    <button
                      type="button"
                      onClick={handleSubmitCode}
                      className="flex w-full items-center justify-center rounded-2xl bg-blue-500 px-4 py-3 text-[16px] font-semibold text-white shadow-[0px_8px_16px_rgba(59,130,246,0.25)] transition-opacity duration-150 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-70"
                      disabled={!code.trim() || isSubmittingCode || isCompletingLogin}
                    >
                      {isSubmittingCode || isCompletingLogin ? 'ログイン処理中…' : 'ログインする'}
                    </button>
                    <button
                      type="button"
                      onClick={resendCode}
                      className="text-[13px] font-semibold text-blue-500 underline-offset-2 hover:underline disabled:text-zinc-400"
                      disabled={!canResend || isSendingEmail || oauthLoading || isCompletingLogin}
                    >
                      コードを再送信
                    </button>
                  </div>
                )}
                {feedback ? (
                  <p
                    className={`text-[13px] font-semibold ${feedback.tone === 'error' ? 'text-red-500' : 'text-blue-600'}`}
                  >
                    {feedback.message}
                  </p>
                ) : null}
                {/**
                <p className="text-[12px] leading-relaxed text-zinc-400">
                  ログインを続行することで、
                  <span className="font-semibold text-zinc-500">利用規約</span>と
                  <span className="font-semibold text-zinc-500">プライバシーポリシー</span>
                  に同意したものとみなされます。
                </p>
                */}
              </motion.div>
            </div>
          </motion.dialog>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

export default LoginSheet
